import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { GoodsReturnsService } from '../goods-returns.service'
import { GoodsReturnsRepository } from '../goods-returns.repository'
import { Order, OrdersService } from '../../orders'
import { User } from '../../users/models/user.entity'
import { UsersService } from '../../users/users.service'
import {
  addGoodsReturnProducts,
  getGoodsReturnUserIds,
  updateGoodsReturnAmounts,
} from '../libs/goods-return.lib'
import { Comment } from '../../../models/util.model'
import { ProductSelection } from '../../products'
import { GoodsReturn } from '../models/goods-return.entity'
import { GoodsReturnChangeStatusAction } from '../models/goods-return.types'

interface GoodsReturnDetailState {
  goodsReturn?: GoodsReturn
  refOrder?: Order
  users: User[]
  uploadUrl?: string
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const GOODS_RETURN_DETAIL_INITIAL_STATE: GoodsReturnDetailState = {
  users: [],
  isLoading: false,
  isModified: false,
}

@Injectable()
export class GoodsReturnDetailStore {
  constructor(
    private goodsReturnsService: GoodsReturnsService,
    private goodsReturnsRepository: GoodsReturnsRepository,
    private ordersService: OrdersService,
    private usersService: UsersService,
  ) {}

  private state = signal<GoodsReturnDetailState>(
    GOODS_RETURN_DETAIL_INITIAL_STATE,
  )

  // Selectors
  readonly goodsReturn = computed(() => this.state().goodsReturn)
  readonly users = computed(() => this.state().users)
  readonly refOrder = computed(() => this.state().refOrder)
  readonly uploadUrl = computed(() => this.state().uploadUrl)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(goodsReturnId?: string): Promise<void> {
    if (!goodsReturnId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.goodsReturnsService.read$(goodsReturnId).subscribe({
        next: async (data) => {
          const extData = await Promise.all([
            this.getUsers(getGoodsReturnUserIds(data)),
            this.getOrder(data.referenceOrder?._id),
          ])
          this.setData(
            data,
            this.state().goodsReturn?._id
              ? this.state().goodsReturn?._id !== data._id
              : false,
            extData,
          )
          resolve()
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  async save(goodsReturn: GoodsReturn, notify?: boolean): Promise<boolean> {
    const zeroQtyError = goodsReturn?.rows.filter(
      (row) =>
        (row.orderedQty ?? 0) == 0 &&
        (row.expectedQty ?? 0) == 0 &&
        (row.returnedQty ?? 0) == 0,
    ).length
    if ((zeroQtyError ?? 0) > 0) {
      this.goodsReturnsRepository.alert('ROWS_ERROR')
      return false
    }

    this.unsetError()

    return new Promise((resolve) => {
      this.goodsReturnsService.upsert$(goodsReturn).subscribe({
        next: (data) => {
          this.setGoodsReturn(data, false)
          if (notify === true) {
            this.goodsReturnsRepository.notify({ code: 'GOODS_RETURN_SAVED' })
          }
          resolve(true)
        },
        error: (error) => {
          this.setError(error)
          resolve(false)
        },
      })
    })
  }

  async cancel(goodsReturn: GoodsReturn): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.goodsReturnsRepository
        .alert('GOODS_RETURN_CANCEL', goodsReturn)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.goodsReturnsService.cancel$(goodsReturn._id).subscribe({
              next: (value) => {
                this.setGoodsReturn(value, false)
                this.goodsReturnsRepository.notify({
                  code: 'GOODS_RETURN_CANCELED',
                })
                resolve(true)
              },
              error: (error: HttpErrorResponse) => {
                this.setError(error)
                resolve(false)
              },
            })
          }
        })
    })
  }

  async changeStatus(
    goodsReturnId: string,
    action: GoodsReturnChangeStatusAction,
  ): Promise<boolean> {
    this.setIsLoading(true)
    return new Promise((resolve) => {
      this.goodsReturnsService.changeStatus$(goodsReturnId, action).subscribe({
        next: (data) => {
          this.goodsReturnsRepository.notify({
            code: 'GOODS_RETURN_PROCESSED',
          })
          this.setGoodsReturn(data, false)
          resolve(true)
        },
        error: (error) => {
          this.setError(error)
          resolve(false)
        },
      })
    })
  }

  async addComments(comment: Comment) {
    const goodsReturn = this.state().goodsReturn
    if (!goodsReturn?._id) {
      return
    }

    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.goodsReturnsService.addComment$(goodsReturn._id, comment).subscribe({
        next: (data: Comment) => {
          goodsReturn.comments = [...(goodsReturn.comments ?? []), data]
          this.load(goodsReturn._id)
          resolve(data)
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  async downloadAttachment(id: string) {
    const goodsReturn = this.state().goodsReturn
    if (!goodsReturn?._id) {
      return
    }

    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.goodsReturnsService
        .downloadAttachment$(goodsReturn._id, id)
        .subscribe({
          next: (response) => {
            const contentDisposition =
              response.headers.get('Content-Disposition') ||
              'attachment; filename="download"'
            const filename =
              contentDisposition.split('filename=')[1] || 'download'
            const blob = response.body

            if (blob) {
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = filename.replace(/"/g, '')
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(url)
              a.remove()
            }
            resolve(response)
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async deleteAttachment(id: string) {
    const goodsReturn = this.state().goodsReturn
    if (!goodsReturn?._id) {
      return
    }

    const saved = await this.save(goodsReturn)

    if (saved) {
      this.setIsLoading(true)
      return new Promise((resolve, reject) => {
        this.goodsReturnsService
          .deleteAttachment$(goodsReturn._id, id)
          .subscribe({
            next: (response) => {
              this.setGoodsReturn(response, false)
              this.goodsReturnsRepository.notify({
                code: 'ATTACHMENT_DELETED',
              })
              resolve(response)
            },
            error: (error) => {
              this.setError(error)
              reject(error)
            },
          })
      })
    } else {
      return
    }
  }

  async getOrder(orderId?: string): Promise<Order | undefined> {
    if (!orderId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.ordersService.read$(orderId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          this.setError(error)
          resolve(undefined)
        },
      })
    })
  }

  async getUsers(userIds?: string[]): Promise<User[] | undefined> {
    if (!userIds?.length) {
      return undefined
    }
    return new Promise((resolve) => {
      this.usersService.list$({ _id: userIds }).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async findRefOrderGoodsReturn(
    orderNumber: number | undefined,
  ): Promise<GoodsReturn[] | undefined> {
    return new Promise((resolve, reject) => {
      this.goodsReturnsService
        .search$({
          'referenceOrder.number': orderNumber,
        })
        .subscribe({
          next: (data) => {
            resolve(data.data)
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  // In goods-return-detail.store.ts, line ~307
  addProducts(products: ProductSelection[]) {
    let goodsReturn = this.state().goodsReturn
    if (!goodsReturn) {
      this.goodsReturnsRepository.notify({ code: 'GOODS_RETURN_NOT_FOUND' })
      return
    }
    goodsReturn = addGoodsReturnProducts(goodsReturn, products)
    this.setGoodsReturn(goodsReturn)
  }

  updateAmounts() {
    setTimeout(() => {
      const goodsReturn = this.goodsReturn()
      if (!goodsReturn) {
        return
      }

      this.state.set({
        ...this.state(),
        goodsReturn: updateGoodsReturnAmounts(goodsReturn),
        isLoading: false,
      })
    }, 1)
  }

  // Reducers

  setData(
    goodsReturn: GoodsReturn,
    isModified?: boolean,
    extData?: [User[] | undefined, Order | undefined],
  ) {
    this.state.set({
      ...this.state(),
      goodsReturn: updateGoodsReturnAmounts(goodsReturn),
      isModified: isModified ?? this.state().isModified,
      uploadUrl: this.goodsReturnsService.goodsRetuAttachmentsUrl(
        goodsReturn._id,
      ),
      users: extData?.[0] ?? [],
      refOrder: extData?.[1],
      isLoading: false,
      error: undefined,
    })
  }

  setGoodsReturn(goodsReturn: GoodsReturn, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      goodsReturn: updateGoodsReturnAmounts(goodsReturn),
      isModified: isModified ?? true,
      uploadUrl: this.goodsReturnsService.goodsRetuAttachmentsUrl(
        goodsReturn._id,
      ),
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
